import React from "react";
import { useNavigate } from "react-router";
import { InfoOutlined, WarningAmber } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, LinearProgress, Tooltip, Typography } from "@mui/material";

import { selectCurrentRole } from "fond/api";
import StackedProgress from "fond/project/solve/StackedProgress";
import { Maybe, QuotaUsageEstimate } from "fond/types";
import { formatDateString } from "fond/utils/dates";
import { useAppSelector } from "fond/utils/hooks";

import { QuotaUsageDetails } from "./hooks/useProjectQuota";

import { Label, LinearProgressLabels, ProgressContainer } from "./usageProgress.styles";

interface IProps {
  endTime: Maybe<string>;
  subscription: QuotaUsageDetails;
  estimatedQuotaUsage?: QuotaUsageEstimate | null;
}

const UsageProgress: React.FC<IProps> = ({
  endTime,
  subscription: { AvailableQuota, Quota, QuotaUsage, DataPackQuota, DataPackUsage },
  estimatedQuotaUsage,
}: IProps) => {
  const navigate = useNavigate();
  const isAdmin = useAppSelector(selectCurrentRole) === "admin";
  const estimatedUsage = estimatedQuotaUsage?.EstimatedQuotaUsage ?? null;
  const totalQuota = Quota + DataPackQuota;
  const totalUsage = QuotaUsage + DataPackUsage;
  const totalBaseRemaining = Quota - QuotaUsage;
  const totalDataPackRemaining = DataPackQuota - DataPackUsage;
  const progress = Math.min(Math.floor((totalUsage / totalQuota) * 100), 100);

  const handleOnClick = () => {
    navigate("/settings/account/subscription");
  };

  return (
    <>
      <Box sx={{ display: "flex", mt: 2, mb: 1 }}>
        <InfoOutlined color="action" sx={{ mr: 0.5 }} />

        <ProgressContainer data-testid="progress">
          <Tooltip
            placement="bottom-start"
            title={
              <>
                Unique prems available in your current subscription
                <br />
                <b>Base</b>: {totalBaseRemaining.toLocaleString()}
                {totalDataPackRemaining > 0 ? (
                  <>
                    <br />
                    <b>Data pack</b>: {totalDataPackRemaining.toLocaleString()}
                  </>
                ) : null}
              </>
            }
          >
            <Box>
              {estimatedUsage === null || AvailableQuota <= 0 ? (
                <LinearProgress variant="determinate" value={progress} color={progress < 100 ? "primary" : "error"} />
              ) : (
                <StackedProgress used={totalUsage} pending={estimatedUsage} maximum={totalQuota} />
              )}
              <LinearProgressLabels>
                <Label data-testid="prem-usage" color="text.secondary">
                  {`${AvailableQuota.toLocaleString()} prems remaining until ${endTime ? formatDateString(endTime) : "the end of your billing cycle"}`}
                </Label>
              </LinearProgressLabels>
            </Box>
          </Tooltip>
          <Label sx={{ mt: 1, color: (theme) => theme.palette.biarri.secondary.grey }} data-testid="estimated-usage">
            {estimatedUsage !== null && (
              <>
                {" "}
                Generating this design will consume <b>{estimatedUsage.toLocaleString()}</b> prems from your subscription.
              </>
            )}
          </Label>
        </ProgressContainer>
      </Box>
      {(estimatedUsage ?? 0) > AvailableQuota && (
        <Alert
          icon={<WarningAmber fontSize="inherit" color="warning" />}
          severity="warning"
          sx={{ textAlign: "left" }}
          data-testid="warning"
          aria-labelledby="insufficient-quota-alert-title"
        >
          <AlertTitle id="insufficient-quota-alert-title">Insufficient quota</AlertTitle>
          <Typography>Generating this design requires more prems than your current subscription has remaining.</Typography>
          {isAdmin && (
            <Button variant="outlined" sx={{ mt: 1 }} onClick={handleOnClick}>
              Buy more
            </Button>
          )}
        </Alert>
      )}
    </>
  );
};

export default UsageProgress;
